/**
 * Phase 10D: Share recommendation via email, WhatsApp, or copy link. Rate limit 5/day.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { Email, WhatsApp, Link as LinkIcon } from '@mui/icons-material';
import { renewalsAPI } from '../../services/api';
import { validateShareForm } from '../../utils/renewalValidation';
import { formatCurrency } from '../../utils/renewalHelpers';
import { getErrorMessage } from '../../utils/renewalErrors';
import { trackRecommendationShared } from '../../utils/analytics';
import t from '../../utils/renewalsTranslations';

const SHARE_LIMIT = 5;
const LAST_SHARE_DATE_KEY = 'last_share_date';
const SHARE_COUNT_KEY = 'share_count_today';

function getCanShare() {
  const today = new Date().toDateString();
  const last = localStorage.getItem(LAST_SHARE_DATE_KEY);
  if (last !== today) {
    localStorage.setItem(SHARE_COUNT_KEY, '0');
    localStorage.setItem(LAST_SHARE_DATE_KEY, today);
  }
  const count = parseInt(localStorage.getItem(SHARE_COUNT_KEY) || '0', 10);
  return count < SHARE_LIMIT;
}

function incrementShareCount() {
  const count = parseInt(localStorage.getItem(SHARE_COUNT_KEY) || '0', 10);
  localStorage.setItem(SHARE_COUNT_KEY, String(count + 1));
}

const locale = (typeof window !== 'undefined' && localStorage.getItem('locale')) || 'en';

export default function ShareDialog({ open, onClose, recommendation }) {
  const [form, setForm] = useState({ recipientEmail: '', personalMessage: '' });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const canShare = getCanShare();

  const policyName = recommendation?.policyName ?? recommendation?.policy_name ?? '';
  const savingsAmount = recommendation?.savingsAmount ?? recommendation?.savings_amount ?? 0;
  const insurerName = recommendation?.insurerName ?? recommendation?.insurer_name ?? '';
  const policyId = recommendation?.policyId ?? recommendation?.policy_id ?? '';
  const recId = recommendation?.id;

  const showToast = (severity, msg) => {
    setToast({ severity, msg });
    setTimeout(() => setToast(null), 3000);
  };

  const shareViaEmail = async () => {
    const validation = validateShareForm(form);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    setErrors({});
    setSending(true);
    try {
      await renewalsAPI.shareRecommendation(recId, {
        method: 'email',
        recipientEmail: form.recipientEmail,
        personalMessage: form.personalMessage,
      });
      incrementShareCount();
      trackRecommendationShared({ recommendationId: recId, shareMethod: 'email', savingsAmount });
      showToast('success', t('renewals.share.sent'));
      onClose();
    } catch (e) {
      if (e.response?.status === 429) showToast('error', getErrorMessage('share_rate_limit', locale));
      else showToast('error', t('renewals.share.failed'));
    } finally {
      setSending(false);
    }
  };

  const shareViaWhatsApp = () => {
    const text = (t('renewals.share.whatsappMessage') || 'Better deal: {{policyName}}. Save {{savings}}/mo with {{insurer}}.')
      .replace('{{policyName}}', policyName)
      .replace('{{savings}}', formatCurrency(savingsAmount, 'USD', locale))
      .replace('{{insurer}}', insurerName);
    const link = `${typeof window !== 'undefined' ? window.location.origin : ''}/renewals/${policyId}${recId ? `?rec=${recId}` : ''}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n\n' + link)}`, '_blank');
    incrementShareCount();
    trackRecommendationShared({ recommendationId: recId, shareMethod: 'whatsapp', savingsAmount });
    onClose();
  };

  const copyLink = async () => {
    const link = `${typeof window !== 'undefined' ? window.location.origin : ''}/renewals/${policyId}${recId ? `?rec=${recId}` : ''}`;
    try {
      await navigator.clipboard.writeText(link);
      showToast('success', t('renewals.share.linkCopied'));
      incrementShareCount();
      trackRecommendationShared({ recommendationId: recId, shareMethod: 'link_copy', savingsAmount });
    } catch {
      showToast('error', 'Could not copy');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('renewals.share.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share this better deal: {policyName}. Save {formatCurrency(savingsAmount)}/mo with {insurerName}.
          </Typography>
          <TextField
            fullWidth
            label={t('renewals.share.email')}
            value={form.recipientEmail}
            onChange={(e) => setForm((f) => ({ ...f, recipientEmail: e.target.value }))}
            error={!!errors.recipientEmail}
            helperText={errors.recipientEmail}
            margin="dense"
          />
          <TextField
            fullWidth
            label="Personal message (optional)"
            multiline
            rows={2}
            value={form.personalMessage}
            onChange={(e) => setForm((f) => ({ ...f, personalMessage: e.target.value.slice(0, 500) }))}
            error={!!errors.personalMessage}
            helperText={errors.personalMessage}
            margin="dense"
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<Email />} onClick={shareViaEmail} disabled={sending || !canShare}>
              Email
            </Button>
            <Button variant="outlined" startIcon={<WhatsApp />} onClick={shareViaWhatsApp} disabled={!canShare}>
              WhatsApp
            </Button>
            <Button variant="outlined" startIcon={<LinkIcon />} onClick={copyLink} disabled={!canShare}>
              Copy Link
            </Button>
          </Box>
          {!canShare && <Typography variant="caption" color="error">Daily share limit (5) reached.</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('renewals.share.cancel')}</Button>
        </DialogActions>
      </Dialog>
      {toast && (
        <Box sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', bgcolor: toast.severity === 'error' ? 'error.main' : 'success.main', color: 'white', px: 2, py: 1, borderRadius: 1 }}>
          {toast.msg}
        </Box>
      )}
    </>
  );
}
